/* eslint-disable import/no-extraneous-dependencies */
import { B2ChatClientCatalog } from '@client-sdk';
import { call, put, select, takeLatest } from '@redux-saga/core/effects';
import {
  CallConsumptionReportCostSeries,
  CallConsumptionReportCountSeries,
  CallConsumptionReportGranularities,
  CallConsumptionReportSummary,
  fetchCallConsumptionMetricsCostSeries,
  fetchCallConsumptionMetricsCostSeriesFailure,
  fetchCallConsumptionMetricsCostSeriesSuccess,
  fetchCallConsumptionMetricsCountSeries,
  fetchCallConsumptionMetricsCountSeriesFailure,
  fetchCallConsumptionMetricsCountSeriesSuccess,
  fetchCallConsumptionMetricsSummary,
  fetchCallConsumptionMetricsSummaryFailure,
  fetchCallConsumptionMetricsSummarySuccess,
} from '@src/reducers/callConsumptionReport';
import {
  getCallConsumptionFiltersGranularity,
  getCallConsumptionSelectedDateFormated,
} from '@src/selectors/callConsumptionReport';
import { B2ChatAPI } from '@src/types/api';
import { ErrorData } from '@types';
import dayjs from 'dayjs';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function* fetchCallConsumptionMetricsSummarySaga() {
  const [startDate, endDate] = (yield select(
    getCallConsumptionSelectedDateFormated
  )) as [string, string];

  if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) return;

  try {
    const response: B2ChatAPI.Response<CallConsumptionReportSummary> =
      yield call(B2ChatClientCatalog.resources.calls.actions.metricsSummary, {
        params: {
          from: startDate,
          to: endDate,
          timezone,
        },
        withJWT: true,
      });

    if (response.error) {
      yield put(fetchCallConsumptionMetricsSummaryFailure(response.error));
    } else {
      yield put(fetchCallConsumptionMetricsSummarySuccess(response.data));
    }
  } catch (error) {
    yield put(fetchCallConsumptionMetricsSummaryFailure(error as ErrorData));
  }
}

function* fetchCallConsumptionMetricsCountSeriesSaga() {
  const [startDate, endDate] = (yield select(
    getCallConsumptionSelectedDateFormated
  )) as [string, string];

  if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) return;

  const granularity = (yield select(
    getCallConsumptionFiltersGranularity
  )) as CallConsumptionReportGranularities;

  try {
    const response: B2ChatAPI.Response<CallConsumptionReportCountSeries> =
      yield call(
        B2ChatClientCatalog.resources.calls.actions.metricsCountSeries,
        {
          params: {
            from: startDate,
            to: endDate,
            timezone,
            granularity: granularity.calls,
          },
          withJWT: true,
        }
      );

    if (response.error) {
      yield put(fetchCallConsumptionMetricsCountSeriesFailure(response.error));
    } else {
      yield put(fetchCallConsumptionMetricsCountSeriesSuccess(response.data));
    }
  } catch (error) {
    yield put(
      fetchCallConsumptionMetricsCountSeriesFailure(error as ErrorData)
    );
  }
}

function* fetchCallConsumptionMetricsCostSeriesSaga() {
  const [startDate, endDate] = (yield select(
    getCallConsumptionSelectedDateFormated
  )) as [string, string];

  if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) return;

  const granularity = (yield select(
    getCallConsumptionFiltersGranularity
  )) as CallConsumptionReportGranularities;

  try {
    const response: B2ChatAPI.Response<CallConsumptionReportCostSeries> =
      yield call(
        B2ChatClientCatalog.resources.calls.actions.metricsCostSeries,
        {
          params: {
            from: startDate,
            to: endDate,
            timezone,
            granularity: granularity.consumption,
          },
          withJWT: true,
        }
      );

    if (response.error) {
      yield put(fetchCallConsumptionMetricsCostSeriesFailure(response.error));
    } else {
      yield put(fetchCallConsumptionMetricsCostSeriesSuccess(response.data));
    }
  } catch (error) {
    yield put(fetchCallConsumptionMetricsCostSeriesFailure(error as ErrorData));
  }
}

export default function* callConsumptionReportSaga() {
  yield takeLatest(
    fetchCallConsumptionMetricsSummary,
    fetchCallConsumptionMetricsSummarySaga
  );

  yield takeLatest(
    fetchCallConsumptionMetricsCountSeries,
    fetchCallConsumptionMetricsCountSeriesSaga
  );

  yield takeLatest(
    fetchCallConsumptionMetricsCostSeries,
    fetchCallConsumptionMetricsCostSeriesSaga
  );
}
